/**
 * Lightweight in-app performance meter for the map-first home.
 *
 * Collects map load time, marker update counts and Places lookup latency with
 * near-zero overhead (plain counters + a subscriber set, no timers). The panel
 * is opt-in: `?perf=1`, `sessionStorage.landoo.perf`, or dev builds.
 */

export interface PerfSnapshot {
  /** ms from navigation start until the Maps libraries resolved. */
  mapLoadMs: number | null;
  /** ms from navigation start until the first tile batch finished painting. */
  firstTilesMs: number | null;
  /** Marker diff passes run since load. */
  markerPasses: number;
  markersCreated: number;
  markersUpdated: number;
  markersRemoved: number;
  /** Duration of the most recent marker diff pass. */
  markerLastMs: number | null;
  /** Live marker/cluster count on the map. */
  markersOnMap: number;
  /** Throttled viewport commits (pan/zoom) applied to React state. */
  viewportCommits: number;
  placesLookups: number;
  placesLastMs: number | null;
  placesAvgMs: number | null;
}

const state: PerfSnapshot = {
  mapLoadMs: null,
  firstTilesMs: null,
  markerPasses: 0,
  markersCreated: 0,
  markersUpdated: 0,
  markersRemoved: 0,
  markerLastMs: null,
  markersOnMap: 0,
  viewportCommits: 0,
  placesLookups: 0,
  placesLastMs: null,
  placesAvgMs: null,
};

let placesTotal = 0;
const listeners = new Set<(s: PerfSnapshot) => void>();
let scheduled = false;

/** Batches notifications to one per frame so metrics never cause churn. */
function emit() {
  if (scheduled || listeners.size === 0) return;
  scheduled = true;
  const flush = () => {
    scheduled = false;
    const snapshot = { ...state };
    listeners.forEach((fn) => fn(snapshot));
  };
  if (typeof requestAnimationFrame === "function") requestAnimationFrame(flush);
  else setTimeout(flush, 16);
}

export function now(): number {
  return typeof performance !== "undefined" ? performance.now() : Date.now();
}

export function getPerf(): PerfSnapshot {
  return { ...state };
}

export function subscribePerf(fn: (s: PerfSnapshot) => void): () => void {
  listeners.add(fn);
  fn({ ...state });
  return () => listeners.delete(fn);
}

export function recordMapLoad(ms: number) {
  state.mapLoadMs = Math.round(ms);
  emit();
}

export function recordFirstTiles(ms: number) {
  if (state.firstTilesMs != null) return;
  state.firstTilesMs = Math.round(ms);
  emit();
}

export function recordMarkerPass(info: {
  created: number;
  updated: number;
  removed: number;
  total: number;
  ms: number;
}) {
  state.markerPasses += 1;
  state.markersCreated += info.created;
  state.markersUpdated += info.updated;
  state.markersRemoved += info.removed;
  state.markersOnMap = info.total;
  state.markerLastMs = Math.round(info.ms * 10) / 10;
  emit();
}

export function recordViewportCommit() {
  state.viewportCommits += 1;
  emit();
}

export function recordPlacesLookup(ms: number) {
  state.placesLookups += 1;
  state.placesLastMs = Math.round(ms);
  placesTotal += ms;
  state.placesAvgMs = Math.round(placesTotal / state.placesLookups);
  emit();
}

export function resetPerf() {
  state.markerPasses = 0;
  state.markersCreated = 0;
  state.markersUpdated = 0;
  state.markersRemoved = 0;
  state.markerLastMs = null;
  state.viewportCommits = 0;
  state.placesLookups = 0;
  state.placesLastMs = null;
  state.placesAvgMs = null;
  placesTotal = 0;
  emit();
}

const PERF_KEY = "landoo.perf";

/** True when the in-app performance panel should be shown. */
export function isPerfEnabled(): boolean {
  if (typeof window === "undefined") return false;
  try {
    if (new URLSearchParams(window.location.search).get("perf") === "1") return true;
    return window.sessionStorage.getItem(PERF_KEY) === "1";
  } catch {
    return false;
  }
}

export function setPerfEnabled(on: boolean) {
  if (typeof window === "undefined") return;
  try {
    if (on) window.sessionStorage.setItem(PERF_KEY, "1");
    else window.sessionStorage.removeItem(PERF_KEY);
  } catch {
    /* storage unavailable — panel stays session-only */
  }
}
